export type ScheduleCsvIssue = {
  line?: number;
  message: string;
};

export type ScheduleCsvParseResult = {
  dates: string[];
  issues: ScheduleCsvIssue[];
};

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

const parseIsoDate = (value: string): Date | null => {
  const match = ISO_DATE.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const isLeapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysInMonth = [
    31,
    isLeapYear ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ];
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth[month - 1]) {
    return null;
  }

  // setUTCFullYear avoids Date.UTC's special handling of years 0-99.
  const parsed = new Date(0);
  parsed.setUTCHours(0, 0, 0, 0);
  parsed.setUTCFullYear(year, month - 1, day);
  return parsed;
};

/**
 * Parse the deliberately narrow schedule CSV contract: one `date` column and
 * strict ISO calendar dates. The backend remains authoritative; this mirrors
 * its validation so admins receive row-level feedback before replacing a draft.
 */
export const parseScheduleCsv = (
  csv: string,
  windowStart: string,
  windowEnd: string,
): ScheduleCsvParseResult => {
  const lines = csv.replace(/^\uFEFF/, '').split(/\r\n|\n|\r/);
  const issues: ScheduleCsvIssue[] = [];
  const dates: string[] = [];
  const firstLine = lines.findIndex(line => line.trim() !== '');

  if (firstLine === -1) {
    return {
      dates,
      issues: [{ message: 'The file is empty; expected a date header.' }],
    };
  }

  const header = lines[firstLine].trim();
  if (header !== 'date') {
    issues.push({
      line: firstLine + 1,
      message: 'Expected exactly one header named date.',
    });
  }

  const firstSeenLine = new Map<string, number>();
  let dataRowCount = 0;

  lines.slice(firstLine + 1).forEach((rawLine, offset) => {
    const line = firstLine + offset + 2;
    const value = rawLine.trim();
    if (!value) return;
    dataRowCount += 1;

    if (value.includes(',')) {
      issues.push({ line, message: 'Expected exactly one date column.' });
      return;
    }

    const parsed = parseIsoDate(value);
    if (!parsed) {
      issues.push({
        line,
        message: `Invalid date ${value}; expected YYYY-MM-DD.`,
      });
      return;
    }

    const seenLine = firstSeenLine.get(value);
    if (seenLine !== undefined) {
      issues.push({
        line,
        message: `Duplicate date ${value}; first used on line ${seenLine}.`,
      });
      return;
    }
    firstSeenLine.set(value, line);

    const weekday = parsed.getUTCDay();
    if (weekday === 0 || weekday === 6) {
      issues.push({ line, message: `${value} is a weekend date.` });
      return;
    }

    if (!(windowStart < value && value < windowEnd)) {
      issues.push({
        line,
        message: `${value} must be strictly inside ${windowStart} to ${windowEnd}.`,
      });
      return;
    }

    dates.push(value);
  });

  if (dataRowCount === 0) {
    issues.push({ message: 'Provide at least one date row.' });
  }

  dates.sort();
  return { dates, issues };
};
